import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Clock, Edit2, Trash2, RefreshCw, Check, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  fetchCalendarEvents,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
} from '../../services/google-calendar.service';

interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  description?: string;
  color?: string;
  startTime?: string;
  endTime?: string;
  /** 'google' = sourced from Google Calendar; 'local' = stored in dashboard config */
  source?: 'google' | 'local';
}

interface CalendarWidgetProps {
  title?: string;
  events?: CalendarEvent[];
  onEventsChange?: (events: CalendarEvent[]) => void;
  viewMode?: 'month' | 'week' | 'day';
  editable?: boolean;
  /** Google Calendar integration */
  googleCalendarEnabled?: boolean;
  googleCalendarId?: string;
}

const DAYS_OF_WEEK = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

export default function CalendarWidget({
  title = 'Calendário',
  events = [],
  onEventsChange,
  viewMode: initialViewMode = 'month',
  editable = true,
  googleCalendarEnabled = false,
  googleCalendarId = 'primary',
}: CalendarWidgetProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>(initialViewMode);
  // Google Calendar events (fetched remotely)
  const [gcalEvents, setGcalEvents] = useState<CalendarEvent[]>([]);
  const [gcalLoading, setGcalLoading] = useState(false);
  const [gcalError, setGcalError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isDayEventsDialogOpen, setIsDayEventsDialogOpen] = useState(false);
  const [isEventFormDialogOpen, setIsEventFormDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '09:00',
    endTime: '10:00',
    color: '#3b82f6',
  });

  // ── Google Calendar sync ────────────────────────────────────────────────────
  const loadGcalEvents = async () => {
    if (!googleCalendarEnabled) return;
    setGcalLoading(true);
    setGcalError(null);
    try {
      const timeMin = new Date();
      timeMin.setMonth(timeMin.getMonth() - 1);
      const timeMax = new Date();
      timeMax.setMonth(timeMax.getMonth() + 3);

      const raw = await fetchCalendarEvents(
        googleCalendarId || 'primary',
        timeMin.toISOString(),
        timeMax.toISOString(),
      );

      const converted: CalendarEvent[] = raw.map((e) => ({
        id: `gcal-${e.id}`,
        title: e.title,
        description: e.description,
        date: new Date(e.start),
        startTime: e.allDay ? undefined : e.start.slice(11, 16),
        endTime: e.allDay ? undefined : e.end?.slice(11, 16),
        color: e.color || '#4285f4',
        source: 'google',
      }));
      setGcalEvents(converted);
    } catch (err: any) {
      setGcalError(err.message || 'Erro ao carregar eventos do Google Calendar');
    } finally {
      setGcalLoading(false);
    }
  };

  useEffect(() => {
    loadGcalEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleCalendarEnabled, googleCalendarId]);

  // Merge local + google events for display
  const allEvents = [...events, ...gcalEvents];

  const getMonthData = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const totalDays = lastDay.getDate();

    return { year, month, startingDayOfWeek, totalDays };
  };

  const { year, month, startingDayOfWeek, totalDays } = getMonthData(currentDate);

  // Obter dados da semana atual
  const getWeekData = (date: Date) => {
    const day = date.getDay();
    const diff = date.getDate() - day; // Domingo da semana
    const weekStart = new Date(date.getFullYear(), date.getMonth(), diff);
    const weekDays: Date[] = [];
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      weekDays.push(day);
    }
    
    return weekDays;
  };

  // Navegação
  const goToPrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(year, month - 1, 1));
    } else if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 1);
      setCurrentDate(newDate);
    }
  };

  const goToNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(year, month + 1, 1));
    } else if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 1);
      setCurrentDate(newDate);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Verificar se uma data tem eventos
  const getEventsForDate = (date: Date) => {
    return allEvents
      .filter(event => {
        const eventDate = new Date(event.date);
        return (
          eventDate.getDate() === date.getDate() &&
          eventDate.getMonth() === date.getMonth() &&
          eventDate.getFullYear() === date.getFullYear()
        );
      })
      .sort((a, b) => {
        const timeA = a.startTime || '99:99';
        const timeB = b.startTime || '99:99';
        return timeA.localeCompare(timeB);
      });
  };

  // Abrir dialog para ver compromissos do dia
  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
    setIsDayEventsDialogOpen(true);
  };

  // Abrir dialog para adicionar/editar evento
  const handleAddEvent = (date: Date) => {
    if (!editable) return;
    setSelectedDate(date);
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      startTime: '09:00',
      endTime: '10:00',
      color: '#3b82f6',
    });
    setIsDayEventsDialogOpen(false);
    setIsEventFormDialogOpen(true);
  };

  const handleEditEvent = (event: CalendarEvent) => {
    if (!editable) return;
    setSelectedDate(new Date(event.date));
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      startTime: event.startTime || '09:00',
      endTime: event.endTime || '10:00',
      color: event.color || '#3b82f6',
    });
    setIsDayEventsDialogOpen(false);
    setIsEventFormDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!selectedDate || !formData.title.trim()) return;

    const isGoogleEvent = editingEvent?.source === 'google';
    const isGoogleEnabled = googleCalendarEnabled;

    if (isGoogleEnabled && (isGoogleEvent || !editingEvent)) {
      // ── Google Calendar path ────────────────────────────────────────────────
      try {
        const startDT = formData.startTime
          ? `${selectedDate.toISOString().slice(0, 10)}T${formData.startTime}:00`
          : selectedDate.toISOString().slice(0, 10);
        const endDT = formData.endTime
          ? `${selectedDate.toISOString().slice(0, 10)}T${formData.endTime}:00`
          : startDT;

        const payload = {
          title: formData.title,
          description: formData.description,
          start: startDT,
          end: endDT,
          allDay: !formData.startTime,
          color: formData.color,
        };

        if (isGoogleEvent && editingEvent) {
          // strip 'gcal-' prefix to get the real Google event id
          const gId = editingEvent.id.replace(/^gcal-/, '');
          await updateCalendarEvent(googleCalendarId || 'primary', gId, payload);
        } else {
          await createCalendarEvent(googleCalendarId || 'primary', payload);
        }
        await loadGcalEvents();
      } catch (err: any) {
        setGcalError(err.message);
      }
    } else {
      // ── Local path ──────────────────────────────────────────────────────────
      const newEvent: CalendarEvent = {
        id: editingEvent?.id || `event-${Date.now()}`,
        title: formData.title,
        date: selectedDate,
        description: formData.description,
        startTime: formData.startTime,
        endTime: formData.endTime,
        color: formData.color,
        source: 'local',
      };
      const updatedEvents = editingEvent
        ? events.map(e => e.id === editingEvent.id ? newEvent : e)
        : [...events, newEvent];
      onEventsChange?.(updatedEvents);
    }
    setIsEventFormDialogOpen(false);
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent) return;
    if (editingEvent.source === 'google' && googleCalendarEnabled) {
      try {
        const gId = editingEvent.id.replace(/^gcal-/, '');
        await deleteCalendarEvent(googleCalendarId || 'primary', gId);
        await loadGcalEvents();
      } catch (err: any) {
        setGcalError(err.message);
      }
    } else {
      const updatedEvents = events.filter(e => e.id !== editingEvent.id);
      onEventsChange?.(updatedEvents);
    }
    setIsEventFormDialogOpen(false);
  };

  // Renderizar grade do calendário
  const renderCalendarGrid = () => {
    const days: JSX.Element[] = [];
    const today = new Date();

    // Adicionar dias vazios do mês anterior
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="min-h-[80px] p-1.5 border-r border-b border-border/50" />
      );
    }

    // Adicionar dias do mês
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);
      const dayEvents = getEventsForDate(date);
      const isToday =
        today.getDate() === day &&
        today.getMonth() === month &&
        today.getFullYear() === year;
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;

      days.push(
        <div
          key={day}
          className={cn(
            "min-h-[80px] p-1.5 border-r border-b border-border/50 cursor-pointer hover:bg-accent/50 transition-colors",
            isWeekend && "bg-muted/30",
            isToday && "bg-primary/10"
          )}
          onClick={() => handleDayClick(date)}
        >
          <div className="flex flex-col h-full gap-0.5">
            <div className={cn(
              "text-sm font-medium mb-0.5",
              isToday && "text-primary font-bold"
            )}>
              {day}
            </div>
            {dayEvents.slice(0, 3).map((event) => (
              <div
                key={event.id}
                className="truncate text-[11px] font-medium px-1 py-0.5 rounded text-white leading-tight"
                style={{ backgroundColor: event.color || '#3b82f6' }}
                title={event.title}
              >
                {event.startTime && <span className="opacity-80 mr-1">{event.startTime}</span>}
                {event.title}
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="text-[11px] text-muted-foreground px-1">
                +{dayEvents.length - 3} mais
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // Renderizar visualização de semana
  const renderWeekView = () => {
    const weekDays = getWeekData(currentDate);
    const today = new Date();

    return (
      <div className="border rounded-lg overflow-hidden h-full flex flex-col">
        {/* Cabeçalho com dias da semana */}
        <div className="flex border-b-2 flex-shrink-0">
          {/* Espaçador para alinhar com coluna de horários */}
          <div className="w-16 flex-shrink-0 bg-muted/50 border-r-2"></div>
          
          {/* Dias da semana */}
          <div className="flex-1 grid grid-cols-7">
            {weekDays.map((day, idx) => {
              const isToday = 
                day.getDate() === today.getDate() &&
                day.getMonth() === today.getMonth() &&
                day.getFullYear() === today.getFullYear();
              
              return (
                <div
                  key={idx}
                  className={cn(
                    "p-3 text-center border-r last:border-r-0 bg-muted",
                    isToday && "bg-primary/20"
                  )}
                >
                  <div className="text-xs text-muted-foreground">{DAYS_OF_WEEK[day.getDay()]}</div>
                  <div className={cn(
                    "text-lg font-semibold",
                    isToday && "text-primary"
                  )}>
                    {day.getDate()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline com eventos */}
        <div className="overflow-y-auto h-full">
          <div className="relative">
            {/* Horários */}
            {Array.from({ length: 24 }, (_, hour) => (
              <div key={hour} className="flex border-b h-[60px]">
                {/* Coluna de horário */}
                <div className="w-16 flex-shrink-0 text-xs font-semibold text-muted-foreground bg-muted/50 border-r-2 flex flex-col justify-between py-1 px-2">
                  <div>{hour.toString().padStart(2, '0')}:00</div>
                  <div className="text-[10px] font-normal opacity-60">{hour.toString().padStart(2, '0')}:30</div>
                </div>
                
                {/* Colunas de dias */}
                {weekDays.map((day, dayIdx) => (
                  <div
                    key={dayIdx}
                    className="flex-1 border-r last:border-r-0 relative bg-background"
                    onClick={() => handleDayClick(day)}
                  >
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute top-2/4 left-0 right-0 h-px bg-border/40" />
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {/* Eventos posicionados */}
            {weekDays.map((day, dayIdx) => {
              const dayEvents = getEventsForDate(day);
              const dayWidth = `calc((100% - 64px) / 7)`; // 64px = largura coluna horário
              
              return dayEvents.map((event, eventIdx) => {
                if (!event.startTime || !event.endTime) return null;
                
                const [startHour, startMin] = event.startTime.split(':').map(Number);
                const [endHour, endMin] = event.endTime.split(':').map(Number);
                const startMinutes = startHour * 60 + startMin;
                const endMinutes = endHour * 60 + endMin;
                const durationMinutes = endMinutes - startMinutes;
                
                const top = startMinutes;
                const height = Math.max(durationMinutes, 30);
                
                return (
                  <div
                    key={`${day.toISOString()}-${event.id}`}
                    className="absolute rounded border shadow-sm transition-all cursor-pointer hover:shadow-md"
                    style={{
                      top: `${top}px`,
                      left: `calc(64px + ${dayWidth} * ${dayIdx} + 4px)`,
                      width: `calc(${dayWidth} - 8px)`,
                      height: `${height}px`,
                      backgroundColor: event.color || '#3b82f6',
                      color: 'white',
                      zIndex: 10 + eventIdx
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditEvent(event);
                    }}
                  >
                    <div className="p-1.5 h-full overflow-hidden">
                      <div className="font-medium text-xs truncate">{event.title}</div>
                      <div className="text-[10px] opacity-90 truncate">
                        {event.startTime} - {event.endTime}
                      </div>
                    </div>
                  </div>
                );
              });
            })}
          </div>
        </div>
      </div>
    );
  };

  // Renderizar visualização de dia
  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);

    return (
      <div className="border rounded-lg overflow-hidden h-full flex flex-col">
        <div className="overflow-y-auto flex-1">
          <div className="relative">
            {/* Horários */}
            {Array.from({ length: 24 }, (_, i) => {
              const hour = i;
              const hourStr = hour.toString().padStart(2, '0') + ':00';
              
              return (
                <div key={hour} className="flex border-b-2 border-border hover:bg-accent/30 transition-colors h-[60px]">
                  <div className="w-20 flex-shrink-0 text-sm font-semibold text-muted-foreground bg-muted/50 border-r-2 border-border flex flex-col justify-between py-1">
                    <div className="px-3">{hourStr}</div>
                    <div className="px-3 text-xs font-normal opacity-60">
                      {hour.toString().padStart(2, '0')}:30
                    </div>
                  </div>
                  
                  <div className="flex-1 h-[60px] relative bg-background">
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute top-1/4 left-0 right-0 h-px bg-border/20" />
                      <div className="absolute top-2/4 left-0 right-0 h-px bg-border/40" />
                      <div className="absolute top-3/4 left-0 right-0 h-px bg-border/20" />
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Eventos posicionados */}
            {dayEvents.map((event, idx) => {
              if (!event.startTime || !event.endTime) return null;
              
              const [startHour, startMin] = event.startTime.split(':').map(Number);
              const [endHour, endMin] = event.endTime.split(':').map(Number);
              const startMinutes = startHour * 60 + startMin;
              const endMinutes = endHour * 60 + endMin;
              const durationMinutes = endMinutes - startMinutes;
              
              const top = startMinutes;
              const height = Math.max(durationMinutes, 30);
              
              const overlappingEvents = dayEvents.filter(otherEvent => {
                if (otherEvent.id === event.id || !otherEvent.startTime || !otherEvent.endTime) return false;
                const [otherStartHour, otherStartMin] = otherEvent.startTime.split(':').map(Number);
                const [otherEndHour, otherEndMin] = otherEvent.endTime.split(':').map(Number);
                const otherStart = otherStartHour * 60 + otherStartMin;
                const otherEnd = otherEndHour * 60 + otherEndMin;
                return startMinutes < otherEnd && endMinutes > otherStart;
              });
              
              const isOverlapping = overlappingEvents.length > 0;
              const currentEventIndex = dayEvents.findIndex(e => e.id === event.id);
              const overlappingCount = overlappingEvents.length + 1;
              const overlapIndex = overlappingEvents.filter(e => 
                dayEvents.findIndex(ev => ev.id === e.id) < currentEventIndex
              ).length;
              
              const widthPercent = isOverlapping ? 100 / overlappingCount : 100;
              const leftPercent = isOverlapping ? (overlapIndex * widthPercent) : 0;
              const availableWidth = `calc(100% - 80px - 4px)`;
              
              return (
                <div
                  key={event.id}
                  className={cn(
                    "absolute rounded border shadow-sm transition-all group cursor-pointer",
                    isOverlapping && "ring-2 ring-yellow-500/50"
                  )}
                  style={{
                    top: `${top}px`,
                    left: `calc(80px + (${availableWidth}) * ${leftPercent / 100})`,
                    width: `calc((${availableWidth}) * ${widthPercent / 100} - 4px)`,
                    height: `${height}px`,
                    backgroundColor: event.color || '#3b82f6',
                    color: 'white',
                    zIndex: 10 + idx
                  }}
                >
                  <div className="p-2 h-full flex flex-col overflow-hidden">
                    <div className="flex items-start justify-between gap-2 flex-1 min-h-0">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{event.title}</div>
                        <div className="text-xs opacity-90 flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" />
                          <span className="truncate">{event.startTime} - {event.endTime}</span>
                        </div>
                        {event.description && height > 50 && (
                          <div className="text-xs opacity-80 mt-1 line-clamp-2">{event.description}</div>
                        )}
                      </div>
                      {editable && (
                        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity relative z-20 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 hover:bg-white/20 relative z-20"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleEditEvent(event);
                            }}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 hover:bg-white/20 relative z-20"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const updatedEvents = events.filter(e => e.id !== event.id);
                              onEventsChange?.(updatedEvents);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card className="h-full flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 flex-shrink-0">
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" />
            {title}
            {googleCalendarEnabled && (
              <>
                {gcalLoading && (
                  <Badge variant="secondary" className="gap-1">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Syncing
                  </Badge>
                )}
                {gcalError && !gcalLoading && (
                  <Badge variant="destructive" className="gap-1" title={gcalError}>
                    <X className="w-3 h-3" />
                    Error
                  </Badge>
                )}
                {!gcalLoading && !gcalError && (
                  <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" title={`${gcalEvents.length} eventos sincronizados`}>
                    <Check className="w-3 h-3" />
                    Synced
                  </Badge>
                )}
              </>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Seletor de visualização */}
            <div className="flex gap-1 border rounded-md p-1">
              <Button
                variant={viewMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('month')}
              >
                Mês
              </Button>
              <Button
                variant={viewMode === 'week' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('week')}
              >
                Semana
              </Button>
              <Button
                variant={viewMode === 'day' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('day')}
              >
                Dia
              </Button>
            </div>
            
            <Button variant="outline" size="sm" onClick={goToToday}>
              Hoje
            </Button>
            <Button variant="ghost" size="icon" onClick={goToPrevious}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[140px] text-center">
              {viewMode === 'month' && `${MONTHS[month]} ${year}`}
              {viewMode === 'week' && (() => {
                const weekDays = getWeekData(currentDate);
                const start = weekDays[0];
                const end = weekDays[6];
                if (start.getMonth() === end.getMonth()) {
                  return `${start.getDate()}-${end.getDate()} ${MONTHS[start.getMonth()]}`;
                }
                return `${start.getDate()} ${MONTHS[start.getMonth()]} - ${end.getDate()} ${MONTHS[end.getMonth()]}`;
              })()}
              {viewMode === 'day' && currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
            <Button variant="ghost" size="icon" onClick={goToNext}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            {editable && (
              <Button
                size="sm"
                onClick={() => handleAddEvent(viewMode === 'day' ? currentDate : new Date())}
                className="ml-2"
              >
                <Plus className="w-4 h-4 mr-1" />
                Evento
              </Button>
            )}
            {googleCalendarEnabled && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={loadGcalEvents}
                disabled={gcalLoading}
                title="Sincronizar com Google Calendar"
                className="ml-1"
              >
                <RefreshCw className={cn('w-4 h-4', gcalLoading && 'animate-spin')} />
              </Button>
            )}
          </div>
        </CardHeader>
        {gcalError && (
          <div className="mx-4 mb-2 px-3 py-2 text-xs rounded-md bg-destructive/10 text-destructive border border-destructive/30">
            Google Calendar: {gcalError}
          </div>
        )}
        <CardContent className="flex-1 overflow-hidden">
          {viewMode === 'month' && (
            <div className="h-full flex flex-col border-l border-t border-border/50">
              {/* Cabeçalho dos dias da semana */}
              <div className="grid grid-cols-7 flex-shrink-0">
                {DAYS_OF_WEEK.map(day => (
                  <div
                    key={day}
                    className="p-2 text-center text-sm font-semibold border-r border-b border-border/50 bg-muted"
                  >
                    {day}
                  </div>
                ))}
              </div>
              {/* Grade do calendário */}
              <div className="grid grid-cols-7 flex-1 overflow-auto">
                {renderCalendarGrid()}
              </div>
            </div>
          )}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'day' && renderDayView()}
        </CardContent>
      </Card>

      {/* Dialog para ver compromissos do dia */}
      <Dialog open={isDayEventsDialogOpen} onOpenChange={setIsDayEventsDialogOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              Compromissos do Dia
            </DialogTitle>
            <DialogDescription>
              {selectedDate && selectedDate.toLocaleDateString('pt-BR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {selectedDate && getEventsForDate(selectedDate).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nenhum compromisso para este dia</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[65vh] overflow-y-auto overflow-x-hidden">
                  {/* Timeline view */}
                  <div className="relative min-w-full">
                    {/* Horários */}
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i;
                      const hourStr = hour.toString().padStart(2, '0') + ':00';
                      
                      return (
                        <div key={hour} className="flex border-b-2 border-border hover:bg-accent/30 transition-colors h-[60px]">
                          {/* Coluna de horário */}
                          <div className="w-20 flex-shrink-0 text-sm font-semibold text-muted-foreground bg-muted/50 border-r-2 border-border flex flex-col justify-between py-1">
                            {/* Hora principal */}
                            <div className="px-3">
                              {hourStr}
                            </div>
                          </div>
                          
                          {/* Área de eventos com altura fixa de 60px por hora */}
                          <div className="flex-1 h-[60px] relative bg-background">
                            {/* Grid de minutos para referência visual */}
                            <div className="absolute inset-0 pointer-events-none">
                              <div className="absolute top-1/4 left-0 right-0 h-px bg-border/20" />
                              <div className="absolute top-2/4 left-0 right-0 h-px bg-border/40" />
                              <div className="absolute top-3/4 left-0 right-0 h-px bg-border/20" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Eventos posicionados absolutamente */}
                    {selectedDate && getEventsForDate(selectedDate).map((event, idx) => {
                      if (!event.startTime || !event.endTime) return null;
                      
                      const [startHour, startMin] = event.startTime.split(':').map(Number);
                      const [endHour, endMin] = event.endTime.split(':').map(Number);
                      
                      // Calcular posição em minutos do dia
                      const startMinutes = startHour * 60 + startMin;
                      const endMinutes = endHour * 60 + endMin;
                      const durationMinutes = endMinutes - startMinutes;
                      
                      // Altura de cada hora = 60px
                      const pixelsPerMinute = 60 / 60; // 1px por minuto
                      const top = startMinutes * pixelsPerMinute;
                      const height = Math.max(durationMinutes * pixelsPerMinute, 30); // Mínimo 30px
                      
                      // Detectar sobreposições para posicionar lado a lado
                      const overlappingEvents = selectedDate ? getEventsForDate(selectedDate).filter(otherEvent => {
                        if (otherEvent.id === event.id || !otherEvent.startTime || !otherEvent.endTime) return false;
                        
                        const [otherStartHour, otherStartMin] = otherEvent.startTime.split(':').map(Number);
                        const [otherEndHour, otherEndMin] = otherEvent.endTime.split(':').map(Number);
                        const otherStart = otherStartHour * 60 + otherStartMin;
                        const otherEnd = otherEndHour * 60 + otherEndMin;
                        
                        return startMinutes < otherEnd && endMinutes > otherStart;
                      }) : [];
                      
                      const isOverlapping = overlappingEvents.length > 0;
                      
                      // Calcular posição horizontal se houver sobreposição
                      const currentEventIndex = getEventsForDate(selectedDate).findIndex(e => e.id === event.id);
                      const overlappingCount = overlappingEvents.length + 1;
                      const overlapIndex = overlappingEvents.filter(e => 
                        getEventsForDate(selectedDate).findIndex(ev => ev.id === e.id) < currentEventIndex
                      ).length;
                      
                      const widthPercent = isOverlapping ? 100 / overlappingCount : 100;
                      const leftPercent = isOverlapping ? (overlapIndex * widthPercent) : 0;
                      
                      // Calcular largura disponível (total menos a coluna de horário)
                      const availableWidth = `calc(100% - 80px - 4px)`; // 80px horário + 4px padding
                      
                      return (
                        <div
                          key={event.id}
                          className={cn(
                            "absolute rounded border shadow-sm transition-all group cursor-pointer",
                            isOverlapping && "ring-2 ring-yellow-500/50"
                          )}
                          style={{
                            top: `${top}px`,
                            left: `calc(80px + (${availableWidth}) * ${leftPercent / 100})`,
                            width: `calc((${availableWidth}) * ${widthPercent / 100} - 4px)`,
                            height: `${height}px`,
                            backgroundColor: event.color || '#3b82f6',
                            color: 'white',
                            zIndex: 10 + idx
                          }}
                        >
                          <div className="p-2 h-full flex flex-col overflow-hidden">
                            <div className="flex items-start justify-between gap-2 flex-1 min-h-0">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">
                                  {event.title}
                                </div>
                                <div className="text-xs opacity-90 flex items-center gap-1 mt-0.5">
                                  <Clock className="w-3 h-3" />
                                  <span className="truncate">
                                    {event.startTime} - {event.endTime}
                                  </span>
                                </div>
                                {event.description && height > 50 && (
                                  <div className="text-xs opacity-80 mt-1 line-clamp-2">
                                    {event.description}
                                  </div>
                                )}
                              </div>
                              {editable && (
                                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity relative z-20 flex-shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 hover:bg-white/20 relative z-20"
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleEditEvent(event);
                                    }}
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 hover:bg-white/20 relative z-20"
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      const updatedEvents = events.filter(e => e.id !== event.id);
                                      onEventsChange?.(updatedEvents);
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {editable && (
              <Button onClick={() => selectedDate && handleAddEvent(selectedDate)}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Compromisso
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para adicionar/editar eventos */}
      <Dialog open={isEventFormDialogOpen} onOpenChange={setIsEventFormDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? 'Editar Evento' : 'Novo Evento'}
            </DialogTitle>
            <DialogDescription>
              {selectedDate && selectedDate.toLocaleDateString('pt-BR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Reunião com cliente"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime">Hora Início</Label>
                <Input
                  id="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime">Hora Fim</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detalhes do evento"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="color">Cor</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-20 h-10"
                />
                <span className="text-sm text-muted-foreground">{formData.color}</span>
              </div>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            {editingEvent && (
              <Button variant="destructive" onClick={handleDeleteEvent}>
                Deletar
              </Button>
            )}
            <Button variant="outline" onClick={() => setIsEventFormDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEvent} disabled={!formData.title.trim()}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
